import React from 'react';
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl';
import { Stop as StopType, SegmentGroupSegmentGroupTypeEnum } from '@kiwicom/openapi-sdk/mmb';
import ItinerarySegmentStop from '@kiwicom/orbit-components/lib/Itinerary/ItinerarySegment/ItinerarySegmentStop';
import { Status as OrbitItineraryStatus } from '@kiwicom/orbit-components/lib/Itinerary/ItineraryStatus/types.d';
import { useIntl as useIntlNitro } from '@kiwicom/nitro/lib/services/intl/context';
import AlertCircle from '@kiwicom/orbit-components/lib/icons/AlertCircle';
import formatCityAndCode from '../services/helpers/formatCityAndCode';

type StopProps = {
  stop: StopType;
  type: SegmentGroupSegmentGroupTypeEnum;
  // This property has to be named hidden because of the Orbit's implementation. (More details in the docstring below)
  // eslint-disable-next-line @kiwicom/mmb/boolean-type-naming
  hidden?: boolean;
};

/**
 * Component which renders single stop in Itinerary. It can contain city, station, time and date. In case of a schedule change
 * it will display original values as strikethrough text with colored background based on type.
 *
 * @param stop - {@link StopType} object coming from backend
 * @param type - Type of segment group which this stop belongs to (this will be used for styling in case of a schedule change).
 * @param hidden - If true, stop will be rendered as hidden (this will be used for styling in case of a hidden city). This property
 * has to be named hidden because of the Orbit's implementation:
 * Orbit is checking for the hidden property in the children of the ItinerarySegment component in order to apply some context provider
 * and styling to them. If the property has a different name, then the next HiddenStop wouldn't be styled properly.
 *
 * @returns Stop component
 */
const Stop = ({ stop, type, hidden }: StopProps) => {
  const intl = useIntl();
  const { language } = useIntlNitro();
  const { city, code, station, datetimeLocal, originalValues, warning } = stop;
  const isRtl = language.direction === 'rtl';

  const codeOrCityChanged = Boolean(originalValues?.code) || Boolean(originalValues?.city);
  const isDateChanged =
    originalValues?.datetimeLocal &&
    intl.formatDate(datetimeLocal, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    }) !==
      intl.formatDate(originalValues.datetimeLocal, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      });

  return (
    <ItinerarySegmentStop
      city={formatCityAndCode(isRtl, city, code)}
      station={station}
      date={<FormattedDate value={datetimeLocal} weekday="short" month="numeric" day="numeric" />}
      time={<FormattedTime value={datetimeLocal} hour="2-digit" minute="2-digit" />}
      cancelledCity={
        codeOrCityChanged &&
        formatCityAndCode(isRtl, originalValues?.city ?? city, originalValues?.code ?? code)
      }
      cancelledStation={originalValues?.station}
      cancelledDate={
        isDateChanged && (
          <FormattedDate
            value={originalValues.datetimeLocal}
            weekday="short"
            month="numeric"
            day="numeric"
          />
        )
      }
      cancelledTime={
        originalValues?.datetimeLocal && (
          <FormattedTime value={originalValues.datetimeLocal} hour="2-digit" minute="2-digit" />
        )
      }
      type={warning ? 'warning' : (type as OrbitItineraryStatus)}
      icon={warning ? <AlertCircle ariaHidden /> : null}
      hidden={hidden}
      hiddenCityText={
        <FormattedMessage id="mmb.itinerary.hidden_city_label" defaultMessage="Hidden city" />
      }
    />
  );
};

export default Stop;
